import React, { useEffect, useState } from 'react';
import { FiArrowLeft, FiArrowRight, FiX } from 'react-icons/fi';
import { Carpool } from '../../../../model/Carpool';
import { fetchCarpools, deleteCarpool } from '../../../../services/api';
import Modal from '../../../../components/Modal';
import PageHeader from '../../../../components/PageHeader';
import './CarpoolsEditor.css';
import { Role } from '../../../../model/Roles';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import LoadingPlaceholder from '../../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../../services/tabData';

interface CarpoolsEditorProps {
  sessionToken: string;
  festivalId: string;
  userRoles: string[];
}

interface DeleteConfirmModalProps {
  carpool: Carpool;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ carpool, onConfirm, onCancel }) => {
  const t = useMessages('carpools');
  const common = useMessages('common');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.deleteFailed);
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={() => !isDeleting && onCancel()} className="carpools-dialog">
      <div className="modal-header">
        <h3>{t.deleteTitle}</h3>
        {!isDeleting && (
          <button type="button" className="btn btn-ghost btn-icon dialog-close" onClick={onCancel} aria-label={common.close}>
            <FiX aria-hidden="true" />
          </button>
        )}
      </div>
      {error && <div className="modal-error">{error}</div>}
      <p>{t.deleteConfirm(carpool.user_name)}</p>
      <div className="modal-actions">
        <button
          className="danger"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? t.deleting : common.delete}
        </button>
        <button
          onClick={onCancel}
          disabled={isDeleting}
        >
          {common.cancel}
        </button>
      </div>
    </Modal>
  );
};

const CarpoolsEditor: React.FC<CarpoolsEditorProps> = ({ sessionToken, festivalId, userRoles }) => {
  const t = useMessages('carpools');
  const common = useMessages('common');
  const { locale } = useLanguage();
  const [carpools, setCarpools] = useState<Carpool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [carpoolToDelete, setCarpoolToDelete] = useState<Carpool | null>(null);

  const isViewerOnly = userRoles.includes(Role.CARPOOLS_VIEWER) &&
                      !userRoles.includes(Role.CARPOOLS) &&
                      !userRoles.includes(Role.ADMIN);

  const loadCarpools = async () => {
    try {
      setIsLoading(true);
      const data = await loadTabData(festivalId, 'carpools', () => fetchCarpools(sessionToken, festivalId));
      setCarpools(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.loadFailed);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCarpools();
  }, [sessionToken, festivalId]);

  const handleDeleteCarpool = async (carpoolId: string) => {
    try {
      await deleteCarpool(sessionToken, festivalId, carpoolId);
      await loadCarpools(); // Refresh the list after deletion
    } catch (err) {
      setError(err instanceof Error ? err.message : t.deleteFailed);
    }
  };

  if (isLoading) {
    return (
      <div className="carpools-editor">
        <PageHeader title={t.title} description={t.description} />
        <LoadingPlaceholder layout="table" />
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(locale);
  };

  return (
    <div className="carpools-editor">
      <PageHeader title={t.title} count={carpools.length} description={t.description} />
      {carpools.length === 0 ? (
        <div className="empty-state">{t.empty}</div>
      ) : (
        <div className="data-table">
          <div className="data-table-scroll">
            <div className={`carpools-table${isViewerOnly ? ' read-only' : ''}`} role="table" aria-label={t.title}>
              <div className="data-table-head" role="row">
                <span role="columnheader">{t.created}</span>
                <span role="columnheader">{t.direction}</span>
                <span role="columnheader">{t.departure}</span>
                <span role="columnheader">{t.user}</span>
                <span role="columnheader">{t.phone}</span>
                <span role="columnheader">{t.note}</span>
                <span role="columnheader">{t.coordinates}</span>
                {!isViewerOnly && <span role="columnheader" className="carpools-actions-head">{t.actions}</span>}
              </div>
              {carpools.map(carpool => (
                <div className="data-table-row" role="row" key={carpool.identifier}>
                  <span role="cell" className="carpool-date">{formatDate(carpool.created_at)}</span>
                  <span role="cell">
                    <span
                      className="direction-pill"
                      title={carpool.direction === 'from' ? t.fromFestival : t.toFestival}
                    >
                      {carpool.direction === 'from'
                        ? <FiArrowLeft aria-hidden="true" />
                        : <FiArrowRight aria-hidden="true" />}
                      {t.directionLabel(carpool.direction)}
                    </span>
                  </span>
                  <span role="cell" className="carpool-date">{formatDate(carpool.departure_date)}</span>
                  <span role="cell" className="carpool-user">{carpool.user_name}</span>
                  <span role="cell" className="carpool-phone">{carpool.user_phone}</span>
                  <span role="cell" className="carpool-note">{carpool.note}</span>
                  <span role="cell" className="carpool-coordinates">
                    {carpool.external_address_coordinates.latitude.toFixed(6)},
                    {carpool.external_address_coordinates.longitude.toFixed(6)}
                  </span>
                  {!isViewerOnly && (
                    <span role="cell" className="carpool-actions">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => setCarpoolToDelete(carpool)}
                      >
                        {common.delete}
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {carpoolToDelete && (
        <DeleteConfirmModal
          carpool={carpoolToDelete}
          onConfirm={() => handleDeleteCarpool(carpoolToDelete.identifier)}
          onCancel={() => setCarpoolToDelete(null)}
        />
      )}
    </div>
  );
};

export default CarpoolsEditor;
